import { Link } from "react-router-dom";
import { useEmbedMode } from "../layouts/EmbedLayout";

// Embed-aware <Link>. Same surface as react-router-dom's Link but
// rewrites the `to` prop when rendered inside the EmbedLayout subtree
// so internal links stay under `/embed/*`. Outside the embed tree it's
// a pure pass-through.
//
// Why a JSX wrapper rather than just `useTenantNavigate`: breadcrumb
// items render via AntD's `<Breadcrumb items=[{title: <Link.../>}]>`
// pattern, where the link is part of declarative JSX, not a click
// handler. Converting those to `onClick={() => tenantNavigate(...)}`
// would lose the right-click / middle-click "open in new tab"
// behaviour that real anchors give for free.
//
// Spec: IFRAME_REUSE_IMPLEMENTATION_GUIDE.md §8 (extension — was a
// known scope-tight item in the v3.5 ship; this closes it).

const rewrite = (to, isEmbed) => {
  if (!isEmbed) return to;

  // String form: "/income-tax", "/income-tax/notices?type=open", "-1"
  if (typeof to === "string") {
    if (to.startsWith("/embed")) return to;
    if (!to.startsWith("/")) return to; // relative — react-router resolves against current route
    return `/embed${to}`;
  }

  // Object form: { pathname, search, hash, state }
  if (to && typeof to === "object") {
    const pathname = to.pathname;
    if (typeof pathname !== "string") return to;
    if (pathname.startsWith("/embed")) return to;
    if (!pathname.startsWith("/")) return to;
    return { ...to, pathname: `/embed${pathname}` };
  }

  // Numbers (Link to={-1}) / anything else: pass through.
  return to;
};

const TenantLink = ({ to, children, ...rest }) => {
  const { isEmbed } = useEmbedMode();
  return (
    <Link to={rewrite(to, isEmbed)} {...rest}>
      {children}
    </Link>
  );
};

export default TenantLink;
